import {
  signUp,
  signIn as amplifySignIn,
  resetPassword as amplifyResetPassword,
  confirmResetPassword,
  getCurrentUser as amplifyGetCurrentUser,
  resendSignUpCode,
  confirmSignUp as amplifyConfirmSignUp,
  updateUserAttributes as amplifyUpdateUserAttributes,
  verifyTOTPSetup,
  fetchUserAttributes,
  fetchAuthSession,
  updatePassword,
  SignInOutput,
  AuthUser,
} from 'aws-amplify/auth';
import { SignUpData } from 'Types';
import { isNil } from 'lodash';
import { MissingSignUpFormDataError } from 'Utils/Errors';
import { isEmail, isPhoneNumber } from 'Components/Forms/SignUpForm';
import { codeFromCountryAcronym } from 'Components/Forms/CountryCodeSelect';
import { LogInPlayload } from 'Types/AuthTypes';
const userSignUpParams: string[] = ['emailOrPhone', 'name', 'password'];

export const signUpUser = (signUpData: SignUpData): Promise<unknown> => {
  userSignUpParams.forEach((field: string) => {
    if (isNil(signUpData[field as keyof SignUpData])) {
      throw new MissingSignUpFormDataError(field);
    }
  });

  let userAttributes = {
    name: signUpData.name,
  };

  let username = '';

  if (isEmail(signUpData.emailOrPhone)) {
    userAttributes = Object.assign(userAttributes, {
      email: signUpData.emailOrPhone,
    });
    username = signUpData.emailOrPhone;
  }

  if (isPhoneNumber(signUpData.emailOrPhone)) {
    const strippedNumber =
      '+' +
      codeFromCountryAcronym(signUpData.countryAcronym) +
      signUpData.emailOrPhone.replace(/[^0-9.]/g, '');
    userAttributes = Object.assign(userAttributes, {
      phone_number: strippedNumber,
    });
    username = strippedNumber;
  }

  const signUpParams = {
    username,
    password: signUpData.password,
    options: {
      userAttributes,
    },
  };

  return signUp(signUpParams);
};

export const signIn = async (signInData: LogInPlayload): Promise<AuthUser> => {
  // Unfortunately we have a problem here grabbing the "CognitoUser" type from amplify
  const { emailOrPhone, password, countryAcronym } = signInData;
  let signInResult: SignInOutput;
  if (isPhoneNumber(emailOrPhone)) {
    const strippedNumber = emailOrPhone.replace(/[^0-9.]/g, '');
    // on normal sign in, country code is provided. However, when signing in after verification, it's pulled
    // from state w
    const phoneNumberWithCountryCode = countryAcronym
      ? '+' + codeFromCountryAcronym(countryAcronym) + strippedNumber
      : strippedNumber;
    signInResult = await amplifySignIn({ username: phoneNumberWithCountryCode, password });
  } else {
    signInResult = await amplifySignIn({ username: emailOrPhone, password });
  }

  const user = await getCurrentUser();
  if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    // const {requiredAttributes} = user.challengeParam; // the array of required attributes, e.g ['email', 'phone_number']
    // // You need to get the new password and required attributes from the UI inputs
    // // and then trigger the following function with a button click
    // // For example, the email and phone_number are required attributes
    // const {username, email, phone_number} = getInfoFromUserInput();
    // const loggedUser = await Auth.completeNewPassword(
    //     user,              // the Cognito User Object
    //     newPassword,       // the new password
    //     // OPTIONAL, the required attributes
    //     {
    //         email,
    //         phone_number,
    //     }
    // );
    throw new Error('NEW_PASSWORD_REQUIRED');
  } else {
    return user;
  }
};

export const forgotPassword = (username: string, countryAcronym?: string) => {
  if (isPhoneNumber(username) && countryAcronym) {
    const strippedNumber =
      '+' + codeFromCountryAcronym(countryAcronym) + username.replace(/[^0-9.]/g, '');
    return amplifyResetPassword({ username: strippedNumber });
  } else if (isPhoneNumber(username) && !countryAcronym) {
    throw new Error('Country code not specified for phone number');
  } else {
    return amplifyResetPassword({ username });
  }
};

export const submitForgotPassword = (
  username: string,
  code: string,
  newPassword: string,
  countryAcronym?: string,
) => {
  if (isPhoneNumber(username) && countryAcronym) {
    const strippedNumber =
      '+' + codeFromCountryAcronym(countryAcronym) + username.replace(/[^0-9.]/g, '');
    return confirmResetPassword({ username: strippedNumber, confirmationCode: code, newPassword });
  } else if (isPhoneNumber(username) && !countryAcronym) {
    throw new Error('Country code not specified for phone number');
  } else {
    return confirmResetPassword({ username, confirmationCode: code, newPassword });
  }
};

export const getCurrentUser = () => amplifyGetCurrentUser();

export const resendSignUp = (username: string, countryAcronym?: string) => {
  if (isPhoneNumber(username) && countryAcronym) {
    const strippedNumber =
      '+' + codeFromCountryAcronym(countryAcronym) + username.replace(/[^0-9.]/g, '');
    return resendSignUpCode({ username: strippedNumber });
  } else if (isPhoneNumber(username) && !countryAcronym) {
    throw new Error('Country code not specified for phone number');
  } else {
    return resendSignUpCode({ username });
  }
};

export const confirmSignUp = (username: string, code: string, countryAcronym?: string) => {
  if (isPhoneNumber(username) && countryAcronym) {
    const strippedNumber =
      '+' + codeFromCountryAcronym(countryAcronym) + username.replace(/[^0-9.]/g, '');
    return amplifyConfirmSignUp({ username: strippedNumber, confirmationCode: code });
  } else if (isPhoneNumber(username) && !countryAcronym) {
    throw new Error('Country code not specified for phone number');
  } else {
    return amplifyConfirmSignUp({ username, confirmationCode: code });
  }
};

export const updateUserAttributes = async (attributes: Record<string, string>) => {
  return amplifyUpdateUserAttributes({ userAttributes: attributes });
};

export const verifyCurrentUserAttributeSubmit = async (attr: string, code: string) => {
  return verifyTOTPSetup({ code }); // or use the correct function if verifying attribute
};

// No direct equivalent in Amplify Auth v6 for verifiedContact; consider removing or replacing with custom logic if needed.
export const verifiedContact = async (): Promise<{ verified: {}; unverified: {} }> => {
  // Not implemented: No direct equivalent in Amplify Auth v6
  return { verified: {}, unverified: {} };
};

// No direct equivalent in Amplify Auth v6 for verifyCurrentUserAttribute; consider removing or replacing with custom logic if needed.
export const verifyUserAttribute = async (_attribute: string): Promise<void> => {
  // Not implemented: No direct equivalent in Amplify Auth v6
  return;
};

export const userAttributes = async (): Promise<Record<string, string>> => {
  return fetchUserAttributes();
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string,
): Promise<'SUCCESS'> => {
  await updatePassword({ oldPassword, newPassword });
  return 'SUCCESS';
};
export const getCurrentAuthToken = async (): Promise<string | undefined> => {
  try {
    const session = await fetchAuthSession();
    // session.tokens?.accessToken?.toString() is the new way in Amplify v6
    return session.tokens?.accessToken?.toString();
  } catch {
    return undefined;
  }
};
